import React from "react";
import { Box, Text } from "@chakra-ui/react";

export default function FileDragDrop({
  enabled = true,
  disabled = false,
  onFiles,
  hint = "Drop files to upload",
  children,
  borderRadius = "md",
}) {
  const rootRef = React.useRef(null);
  const dragDepthRef = React.useRef(0);

  const setDragging = React.useCallback((dragging) => {
    const el = rootRef.current;
    if (!el) return;
    el.dataset.dragging = dragging ? "true" : "false";
  }, []);

  const isFileDrag = React.useCallback((e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    return types.includes("Files");
  }, []);

  const isAllowed = React.useCallback(
    (e) => enabled && !disabled && isFileDrag(e),
    [enabled, disabled, isFileDrag]
  );

  const handleDragEnter = React.useCallback(
    (e) => {
      if (!isAllowed(e)) return;
      e.preventDefault();
      e.stopPropagation();

      dragDepthRef.current += 1;
      setDragging(true);
    },
    [isAllowed, setDragging]
  );

  const handleDragOver = React.useCallback(
    (e) => {
      if (!isAllowed(e)) return;
      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setDragging(true);
    },
    [isAllowed, setDragging]
  );

  const handleDragLeave = React.useCallback(
    (e) => {
      if (!enabled || disabled) return;
      e.preventDefault();
      e.stopPropagation();

      dragDepthRef.current -= 1;
      if (dragDepthRef.current <= 0) {
        dragDepthRef.current = 0;
        setDragging(false);
      }
    },
    [enabled, disabled, setDragging]
  );

  const handleDrop = React.useCallback(
    (e) => {
      if (!isAllowed(e)) return;
      e.preventDefault();
      e.stopPropagation();

      setDragging(false);
      dragDepthRef.current = 0;

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) onFiles?.(files);
    },
    [isAllowed, onFiles, setDragging]
  );

  return (
    <Box
      ref={rootRef}
      data-dragging="false"
      position="relative"
      borderRadius={borderRadius}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        "&[data-dragging='true']": {
          outline: "2px dashed",
          outlineColor: "blue.400",
          background: "blue.50",
        },
        "&[data-dragging='true'] .fileDragDropOverlay": {
          opacity: 1,
        },
        ".fileDragDropOverlay": {
          opacity: 0,
          transition: "opacity 120ms ease",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        className="fileDragDropOverlay"
        position="absolute"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={2}
      >
        <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="sm">
          <Text fontSize="body.xs" color="blue.700">
            {hint}
          </Text>
        </Box>
      </Box>

      <Box position="relative" zIndex={1}>
        {children}
      </Box>
    </Box>
  );
}
